import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { fmtMYT } from "@/lib/myt";
import { Check, Copy, Eye, ImagePlus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const CATEGORIES = ["Merchandise", "Luxury", "Technology", "Trading Equipment", "Lifestyle", "Experiences"];

const EMPTY_FORM = {
  name: "",
  category: "Merchandise",
  short_description: "",
  full_description: "",
  image_url: "",
  required_rank_level: "",
  stock: "",
  display_order: 0,
  redemption_instructions: "",
  status: "active",
};

const STATUS_STYLES = {
  pending: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30",
  approved: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  processing: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30",
  shipped: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  rejected: "text-red-400 bg-red-400/10 border-red-400/30",
  delivered: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
};

function requirementLabel(reward, ranks) {
  if (!reward.required_rank_level) return "All members";
  const rank = ranks.find((r) => r.level === reward.required_rank_level);
  return `Rank ${rank ? rank.name : `Lv.${reward.required_rank_level}`}+`;
}

export default function AdminRewards() {
  const [rewards, setRewards] = useState(null);
  const [redemptions, setRedemptions] = useState(null);
  const [ranks, setRanks] = useState([]);
  const [modal, setModal] = useState(null); // {mode: 'create'|'edit', form, id?}
  const [preview, setPreview] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [claimDetail, setClaimDetail] = useState(null);
  const [addrCopied, setAddrCopied] = useState(false);
  const [trackingInput, setTrackingInput] = useState("");
  const [trackingBusy, setTrackingBusy] = useState(false);

  const copyAddress = async () => {
    const a = claimDetail?.delivery_address || {};
    const lines = [
      a.recipient_name,
      a.address_line1,
      a.address_line2,
      [a.city, a.state, a.postal_code].filter(Boolean).join(", "),
      a.country,
      a.phone || claimDetail?.phone ? `Phone: ${a.phone || claimDetail?.phone}` : null,
      a.email ? `Email: ${a.email}` : null,
    ]
      .filter(Boolean)
      .join("\n");
    try {
      await navigator.clipboard.writeText(lines);
      setAddrCopied(true);
      toast.success("Full delivery address copied");
      setTimeout(() => setAddrCopied(false), 2000);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  const saveTracking = async () => {
    if (!claimDetail) return;
    setTrackingBusy(true);
    try {
      await api.patch(`/admin/redemptions/${claimDetail.id}`, {
        status: claimDetail.status,
        tracking_number: trackingInput,
      });
      toast.success("Tracking number saved");
      const saved = trackingInput.trim() || null;
      setClaimDetail((d) => ({ ...d, tracking_number: saved }));
      setRedemptions((rs) => (rs || []).map((x) => (x.id === claimDetail.id ? { ...x, tracking_number: saved } : x)));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTrackingBusy(false);
    }
  };

  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/admin/rewards").then((res) => setRewards(res.data)).catch(() => setRewards([]));
    api.get("/admin/redemptions").then((res) => setRedemptions(res.data)).catch(() => setRedemptions([]));
    api.get("/admin/ranks").then((res) => setRanks(res.data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const location = useLocation();
  useEffect(() => {
    if (location.state?.focus === "redemptions" && redemptions !== null) {
      document.getElementById("redemption-requests-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.state, redemptions]);

  const openCreate = () => {
    setError("");
    setModal({ mode: "create", form: { ...EMPTY_FORM, display_order: (rewards?.length || 0) + 1 } });
  };

  const openEdit = (reward) => {
    setError("");
    setModal({
      mode: "edit",
      id: reward.id,
      form: {
        name: reward.name,
        category: reward.category || "Merchandise",
        short_description: reward.short_description || "",
        full_description: reward.full_description || "",
        image_url: reward.image_url || "",
        required_rank_level: reward.required_rank_level ?? "",
        stock: reward.stock ?? "",
        display_order: reward.display_order ?? 0,
        redemption_instructions: reward.redemption_instructions || "",
        status: reward.status,
      },
    });
  };

  const setField = (key) => (e) =>
    setModal((m) => ({ ...m, form: { ...m.form, [key]: e.target.value } }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/admin/uploads/image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setModal((m) => ({ ...m, form: { ...m.form, image_url: data.url } }));
      toast.success("Image uploaded");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSave = async () => {
    const { mode, id, form } = modal;
    setLoading(true);
    setError("");
    try {
      const payload = {
        name: form.name,
        category: form.category,
        short_description: form.short_description,
        full_description: form.full_description,
        image_url: form.image_url || null,
        required_rank_level: form.required_rank_level === "" ? null : Number(form.required_rank_level),
        stock: form.stock === "" ? null : Number(form.stock),
        display_order: Number(form.display_order) || 0,
        redemption_instructions: form.redemption_instructions,
        status: form.status,
      };
      if (mode === "create") {
        await api.post("/admin/rewards", payload);
        toast.success("Reward created");
      } else {
        await api.put(`/admin/rewards/${id}`, payload);
        toast.success("Reward updated");
      }
      setModal(null);
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const toggleReward = async (reward) => {
    try {
      await api.put(`/admin/rewards/${reward.id}`, {
        name: reward.name,
        category: reward.category,
        short_description: reward.short_description,
        full_description: reward.full_description,
        image_url: reward.image_url,
        required_rank_level: reward.required_rank_level,
        stock: reward.stock,
        display_order: reward.display_order,
        redemption_instructions: reward.redemption_instructions,
        status: reward.status === "active" ? "inactive" : "active",
      });
      toast.success(`Reward ${reward.status === "active" ? "deactivated" : "activated"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async (reward) => {
    setBusyId(reward.id);
    try {
      const { data } = await api.delete(`/admin/rewards/${reward.id}`);
      toast.success(data.message);
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const updateRedemption = async (redemption, status) => {
    setBusyId(redemption.id);
    try {
      await api.patch(`/admin/redemptions/${redemption.id}`, { status });
      toast.success(`Request ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-rewards-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="eyebrow-label mb-2">Management</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Reward Management</h1>
          <p className="mt-2 text-sm text-[#64748B]">
            Curate the Reward Mall catalog and review member redemption requests.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => document.getElementById("redemption-requests-section")?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="px-6 py-2.5 text-sm rounded-lg border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
            data-testid="reward-redemptions-shortcut-button"
          >
            Redemptions
          </button>
          <button onClick={openCreate} className="btn-gold px-6 py-2.5 text-sm shrink-0" data-testid="reward-create-open-button">
            + Create Reward
          </button>
        </div>
      </div>

      <div className="card-lux overflow-hidden">
        <div className="px-6 pt-6"><p className="eyebrow-label">Rewards Catalog</p></div>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm" data-testid="admin-rewards-table">
            <thead>
              <tr className="border-b border-[#222530] text-left">
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Reward</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden lg:table-cell">Category</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Requirement</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden md:table-cell">Order</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Status</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#222530]">
              {rewards === null ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#64748B]">Loading rewards...</td></tr>
              ) : rewards.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#64748B]" data-testid="admin-rewards-empty">No rewards created yet.</td></tr>
              ) : (
                rewards.map((r) => (
                  <tr key={r.id} className="hover:bg-[#0B0C10]/60 transition-colors" data-testid={`admin-reward-row-${r.id.slice(-6)}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        {r.image_url ? (
                          <img src={r.image_url} alt={r.name} className="h-14 w-14 rounded-lg object-cover border border-[#222530] shrink-0" loading="lazy" data-testid={`admin-reward-thumb-${r.id.slice(-6)}`} />
                        ) : (
                          <div className="h-14 w-14 rounded-lg bg-[#0B0C10] border border-dashed border-[#222530] flex items-center justify-center shrink-0">
                            <ImagePlus className="h-4 w-4 text-[#64748B]" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-white font-medium truncate">{r.name}</p>
                          <p className="text-xs text-[#64748B] truncate max-w-[220px]">{r.short_description}</p>
                          {r.stock !== null && r.stock !== undefined && (
                            <p className="text-[10px] font-mono text-[#D4AF37] mt-0.5">Stock: {r.stock}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell">
                      <span className="text-xs text-[#94A3B8] border border-[#222530] rounded-full px-2.5 py-1">{r.category}</span>
                    </td>
                    <td className="px-6 py-4 font-mono text-[#D4AF37] text-xs">{requirementLabel(r, ranks)}</td>
                    <td className="px-6 py-4 font-mono text-[#94A3B8] text-xs hidden md:table-cell">{r.display_order}</td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${
                        r.status === "active" ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/30" : "text-[#64748B] border-[#222530]"
                      }`} data-testid={`admin-reward-status-${r.id.slice(-6)}`}>{r.status}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <button onClick={() => setPreview(r)} className="text-xs text-[#94A3B8] hover:text-white border border-[#222530] hover:border-[#D4AF37]/40 rounded-lg px-2.5 py-1.5 transition-colors flex items-center gap-1" data-testid={`admin-reward-preview-${r.id.slice(-6)}`}>
                          <Eye className="h-3 w-3" /> Preview
                        </button>
                        <button onClick={() => openEdit(r)} className="text-xs text-[#94A3B8] hover:text-white border border-[#222530] hover:border-[#D4AF37]/40 rounded-lg px-2.5 py-1.5 transition-colors flex items-center gap-1" data-testid={`admin-reward-edit-${r.id.slice(-6)}`}>
                          <Pencil className="h-3 w-3" /> Edit
                        </button>
                        <button onClick={() => toggleReward(r)} className={`text-xs font-medium rounded-lg px-2.5 py-1.5 border transition-colors ${
                          r.status === "active" ? "text-[#94A3B8] border-[#222530] hover:bg-[#121318]" : "text-emerald-400 border-emerald-400/30 hover:bg-emerald-400/10"
                        }`} data-testid={`admin-reward-toggle-${r.id.slice(-6)}`}>
                          {r.status === "active" ? "Deactivate" : "Activate"}
                        </button>
                        {confirmDelete === r.id ? (
                          <button onClick={() => handleDelete(r)} disabled={busyId === r.id} className="text-xs font-medium rounded-lg px-2.5 py-1.5 border text-red-400 border-red-400/50 bg-red-400/10 transition-colors disabled:opacity-50" data-testid={`admin-reward-delete-confirm-${r.id.slice(-6)}`}>
                            {busyId === r.id ? "..." : "Confirm Delete"}
                          </button>
                        ) : (
                          <button onClick={() => { setConfirmDelete(r.id); setTimeout(() => setConfirmDelete(null), 4000); }} className="text-xs text-red-400/80 hover:text-red-400 border border-[#222530] hover:border-red-400/40 rounded-lg px-2.5 py-1.5 transition-colors flex items-center gap-1" data-testid={`admin-reward-delete-${r.id.slice(-6)}`}>
                            <Trash2 className="h-3 w-3" /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-lux overflow-hidden" id="redemption-requests-section">
        <div className="px-6 pt-6"><p className="eyebrow-label">Redemption Requests</p></div>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm" data-testid="admin-redemptions-table">
            <thead>
              <tr className="border-b border-[#222530] text-left">
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Member</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Reward</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden lg:table-cell">Member Rank</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden sm:table-cell">Claimed</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Status</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#222530]">
              {redemptions === null ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#64748B]">Loading requests...</td></tr>
              ) : redemptions.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#64748B]" data-testid="admin-redemptions-empty">No redemption requests yet.</td></tr>
              ) : (
                redemptions.map((r) => (
                  <tr key={r.id} className="hover:bg-[#0B0C10]/60 transition-colors" data-testid={`admin-redemption-row-${r.id.slice(-6)}`}>
                    <td className="px-6 py-4">
                      <p className="text-white font-medium">{r.member_name || r.member?.full_name || "Unknown"}</p>
                      <p className="text-xs text-[#64748B] font-mono">{r.member_uid || r.member?.uid} · {r.member?.username}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {r.reward_image ? (
                          <img src={r.reward_image} alt={r.reward_name} className="h-10 w-10 rounded-lg object-cover border border-[#222530] shrink-0" loading="lazy" />
                        ) : null}
                        <div>
                          <p className="text-white">{r.reward_name}</p>
                          {r.required_rank_name && <p className="text-[10px] font-mono text-[#D4AF37]">Req: {r.required_rank_name}+</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[#D4AF37] text-xs hidden lg:table-cell">{r.member_rank || "—"}</td>
                    <td className="px-6 py-4 text-[#94A3B8] hidden sm:table-cell">
                      {r.created_at ? fmtMYT(r.created_at) : "—"}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${STATUS_STYLES[r.status] || STATUS_STYLES.pending}`} data-testid={`redemption-status-${r.id.slice(-6)}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <button onClick={() => { setClaimDetail(r); setTrackingInput(r.tracking_number || ""); }} className="text-xs text-[#94A3B8] hover:text-white border border-[#222530] hover:border-[#D4AF37]/40 rounded-lg px-2.5 py-1.5 transition-colors flex items-center gap-1" data-testid={`redemption-view-${r.id.slice(-6)}`}>
                          <Eye className="h-3 w-3" /> View
                        </button>
                        {r.status === "pending" && (
                          <>
                            <button onClick={() => updateRedemption(r, "approved")} disabled={busyId === r.id} className="text-xs text-emerald-400 border border-emerald-400/30 hover:bg-emerald-400/10 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-50" data-testid={`redemption-approve-${r.id.slice(-6)}`}>Approve</button>
                            <button onClick={() => updateRedemption(r, "rejected")} disabled={busyId === r.id} className="text-xs text-red-400 border border-red-400/30 hover:bg-red-400/10 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-50" data-testid={`redemption-reject-${r.id.slice(-6)}`}>Reject</button>
                          </>
                        )}
                        {r.status === "approved" && (
                          <button onClick={() => updateRedemption(r, "processing")} disabled={busyId === r.id} className="text-xs text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-50" data-testid={`redemption-process-${r.id.slice(-6)}`}>Mark Processing</button>
                        )}
                        {r.status === "processing" && (
                          <button onClick={() => updateRedemption(r, "shipped")} disabled={busyId === r.id} className="text-xs text-emerald-400 border border-emerald-400/30 hover:bg-emerald-400/10 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-50" data-testid={`redemption-ship-${r.id.slice(-6)}`}>Mark Shipped</button>
                        )}
                        {r.status === "shipped" && (
                          <button onClick={() => updateRedemption(r, "delivered")} disabled={busyId === r.id} className="text-xs text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-50" data-testid={`redemption-deliver-${r.id.slice(-6)}`}>Mark Delivered</button>
                        )}
                        {(r.status === "rejected" || r.status === "delivered") && (
                          <span className="text-xs text-[#64748B]">Closed</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="reward-form-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setModal(null)} />
          <div className="card-lux relative w-full max-w-2xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
            <p className="eyebrow-label mb-2">{modal.mode === "create" ? "Create Reward" : "Edit Reward"}</p>
            <h3 className="font-heading text-lg font-semibold text-white mb-6">
              {modal.mode === "create" ? "New Reward" : modal.form.name}
            </h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Reward Name</label>
                <input className="input-lux" value={modal.form.name} onChange={setField("name")} placeholder="e.g. Gold Trader Watch" data-testid="reward-form-name-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Category</label>
                <select className="input-lux" value={modal.form.category} onChange={setField("category")} data-testid="reward-form-category-select">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Display Order</label>
                <input type="number" className="input-lux font-mono" value={modal.form.display_order} onChange={setField("display_order")} data-testid="reward-form-order-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Rank Requirement</label>
                <select className="input-lux" value={modal.form.required_rank_level} onChange={setField("required_rank_level")} data-testid="reward-form-rank-select">
                  <option value="">No rank requirement</option>
                  {ranks.map((r) => <option key={r.level} value={r.level}>{r.level}. {r.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Stock <span className="text-[#64748B]">(optional)</span></label>
                <input type="number" min="0" className="input-lux font-mono" value={modal.form.stock} onChange={setField("stock")} placeholder="Unlimited" data-testid="reward-form-stock-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Status</label>
                <select className="input-lux" value={modal.form.status} onChange={setField("status")} data-testid="reward-form-status-select">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Reward Image</label>
                <div className="flex items-start gap-4">
                  {modal.form.image_url ? (
                    <img src={modal.form.image_url} alt="Preview" className="h-20 w-20 rounded-lg object-cover border border-[#D4AF37]/30 shrink-0" data-testid="reward-form-image-preview" />
                  ) : (
                    <div className="h-20 w-20 rounded-lg bg-[#0B0C10] border border-dashed border-[#222530] flex items-center justify-center shrink-0">
                      <ImagePlus className="h-5 w-5 text-[#64748B]" />
                    </div>
                  )}
                  <div className="flex-1 space-y-2">
                    <input className="input-lux font-mono text-xs" value={modal.form.image_url} onChange={setField("image_url")} placeholder="Image URL or upload →" data-testid="reward-form-image-url-input" />
                    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} className="block w-full text-xs text-[#94A3B8] file:mr-3 file:rounded-lg file:border file:border-[#D4AF37]/40 file:bg-[#D4AF37]/10 file:px-3 file:py-1.5 file:text-xs file:text-[#D4AF37] hover:file:bg-[#D4AF37]/20" data-testid="reward-form-image-upload" />
                    {uploading && <p className="text-xs text-[#D4AF37]">Uploading...</p>}
                  </div>
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Short Description</label>
                <input className="input-lux" value={modal.form.short_description} onChange={setField("short_description")} placeholder="One-line summary for the reward card" data-testid="reward-form-short-description-input" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Full Description</label>
                <textarea rows={3} className="input-lux resize-y" value={modal.form.full_description} onChange={setField("full_description")} placeholder="Detailed description for the reward detail view" data-testid="reward-form-full-description-input" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Redemption Instructions</label>
                <textarea rows={2} className="input-lux resize-y" value={modal.form.redemption_instructions} onChange={setField("redemption_instructions")} placeholder="How the member receives this reward" data-testid="reward-form-instructions-input" />
              </div>
            </div>
            {error && (
              <p className="mt-5 text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="reward-form-error">{error}</p>
            )}
            <div className="flex gap-3 pt-6">
              <button onClick={handleSave} disabled={loading || uploading} className="btn-gold flex-1 py-2.5 text-sm disabled:opacity-60" data-testid="reward-form-save-button">
                {loading ? "Saving..." : modal.mode === "create" ? "Create Reward" : "Save Changes"}
              </button>
              <button onClick={() => setModal(null)} className="btn-outline-gold px-5 py-2.5 text-sm" data-testid="reward-form-cancel-button">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {preview && (        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="reward-preview-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setPreview(null)} />
          <div className="relative w-full max-w-sm">
            <p className="eyebrow-label mb-3 text-center">Member Preview</p>
            <div className="card-lux overflow-hidden">
              {preview.image_url ? (
                <img src={preview.image_url} alt={preview.name} className="w-full h-52 object-cover" />
              ) : (
                <div className="w-full h-52 bg-[#0B0C10] flex items-center justify-center">
                  <ImagePlus className="h-8 w-8 text-[#222530]" />
                </div>
              )}
              <div className="p-6">
                <p className="eyebrow-label mb-2">{preview.category}</p>
                <h3 className="font-heading text-lg font-semibold text-white">{preview.name}</h3>
                <p className="mt-2 text-xs text-[#94A3B8] leading-relaxed">{preview.short_description}</p>
                <p className="mt-4 font-mono text-xs text-[#D4AF37]">{requirementLabel(preview, ranks)}</p>
              </div>
            </div>
            <button onClick={() => setPreview(null)} className="btn-outline-gold w-full py-2.5 text-sm mt-4" data-testid="reward-preview-close-button">Close Preview</button>
          </div>
        </div>
      )}

      {claimDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="redemption-detail-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setClaimDetail(null)} />
          <div className="card-lux relative w-full max-w-xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <p className="eyebrow-label mb-2">Redemption Details</p>
                <h3 className="font-heading text-lg font-semibold text-white" data-testid="redemption-detail-reward-name">{claimDetail.reward_name}</h3>
                {claimDetail.required_rank_name && (
                  <p className="mt-1 text-[10px] font-mono text-[#D4AF37]">Req: {claimDetail.required_rank_name}+</p>
                )}
              </div>
              <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border shrink-0 ${STATUS_STYLES[claimDetail.status] || STATUS_STYLES.pending}`} data-testid="redemption-detail-status">
                {claimDetail.status}
              </span>
            </div>

            <p className="eyebrow-label mb-3">Member</p>
            <div className="grid grid-cols-2 gap-3 text-xs mb-6">
              <div><p className="text-[#64748B]">Full Name</p><p className="text-white" data-testid="redemption-detail-member-name">{claimDetail.member_name || claimDetail.member?.full_name || "—"}</p></div>
              <div><p className="text-[#64748B]">PlusMaker ID</p><p className="font-mono text-[#D4AF37]" data-testid="redemption-detail-member-uid">{claimDetail.member_uid || claimDetail.member?.uid || "—"}</p></div>
              <div className="col-span-2"><p className="text-[#64748B]">Email</p><p className="text-white break-all" data-testid="redemption-detail-member-email">{claimDetail.delivery_address?.email || claimDetail.member?.email || "—"}</p></div>
              <div><p className="text-[#64748B]">Claimed At Rank</p><p className="text-[#D4AF37]" data-testid="redemption-detail-member-rank">{claimDetail.member_rank || "—"}</p></div>
              <div><p className="text-[#64748B]">Request Date</p><p className="text-white" data-testid="redemption-detail-date">{claimDetail.created_at ? fmtMYT(claimDetail.created_at) : "—"}</p></div>
            </div>

            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="eyebrow-label">Delivery Address</p>
              <button onClick={copyAddress} className="text-xs text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 rounded-lg px-3 py-1.5 transition-colors flex items-center gap-1.5" data-testid="redemption-copy-address-button">
                {addrCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                {addrCopied ? "Copied" : "Copy Address"}
              </button>
            </div>
            <div className="border border-[#D4AF37]/25 bg-[#0B0C10]/60 rounded-xl p-4 text-xs space-y-2.5" data-testid="redemption-detail-address">
              <div><p className="text-[#64748B]">Recipient</p><p className="text-white font-medium" data-testid="redemption-detail-recipient">{claimDetail.delivery_address?.recipient_name || "—"}</p></div>
              <div><p className="text-[#64748B]">Phone</p><p className="font-mono text-white" data-testid="redemption-detail-phone">{claimDetail.delivery_address?.phone || claimDetail.phone || "—"}</p></div>
              <div><p className="text-[#64748B]">Address</p><p className="text-white whitespace-pre-line" data-testid="redemption-detail-address-lines">{[claimDetail.delivery_address?.address_line1, claimDetail.delivery_address?.address_line2].filter(Boolean).join("\n") || "—"}</p></div>
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-[#64748B]">City</p><p className="text-white" data-testid="redemption-detail-city">{claimDetail.delivery_address?.city || "—"}</p></div>
                <div><p className="text-[#64748B]">State / Province</p><p className="text-white" data-testid="redemption-detail-state">{claimDetail.delivery_address?.state || "—"}</p></div>
                <div><p className="text-[#64748B]">Postal / ZIP</p><p className="font-mono text-white" data-testid="redemption-detail-postal">{claimDetail.delivery_address?.postal_code || "—"}</p></div>
                <div><p className="text-[#64748B]">Country</p><p className="text-white" data-testid="redemption-detail-country">{claimDetail.delivery_address?.country || "—"}</p></div>
              </div>
            </div>

            {(claimDetail.status === "shipped" || claimDetail.status === "delivered") && (
              <>
                <p className="eyebrow-label mt-6 mb-3">Shipment Tracking</p>
                {claimDetail.status === "shipped" ? (
                  <div className="flex gap-2">
                    <input
                      className="input-lux flex-1 font-mono"
                      value={trackingInput}
                      onChange={(e) => setTrackingInput(e.target.value)}
                      placeholder="Enter tracking number"
                      data-testid="redemption-tracking-input"
                    />
                    <button onClick={saveTracking} disabled={trackingBusy} className="btn-gold px-5 py-2.5 text-sm shrink-0 disabled:opacity-60" data-testid="redemption-tracking-save-button">
                      {trackingBusy ? "Saving..." : "Save"}
                    </button>
                  </div>
                ) : (
                  <p className="border border-[#222530] rounded-lg px-4 py-3 text-sm font-mono text-white" data-testid="redemption-tracking-display">
                    {claimDetail.tracking_number || "—"}
                  </p>
                )}
              </>
            )}

            {claimDetail.admin_note && (
              <p className="mt-4 text-xs text-[#94A3B8] border border-[#222530] rounded-lg px-4 py-3" data-testid="redemption-detail-admin-note">Admin note: {claimDetail.admin_note}</p>
            )}

            <button onClick={() => setClaimDetail(null)} className="btn-outline-gold w-full py-2.5 text-sm mt-6" data-testid="redemption-detail-close-button">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
